import { useState, useEffect, useCallback } from "react";

// Minimum and maximum dimensions
const MIN_WIDTH = 400;
const MIN_HEIGHT = 300;

/**
 * Hook for managing floating output window
 * @returns {object} Floating window state and handlers
 */
export const useFloatingWindow = () => {
  const [isOutputFloating, setIsOutputFloating] = useState(false);
  const [floatingPosition, setFloatingPosition] = useState({ x: 100, y: 0 });
  const [floatingSize, setFloatingSize] = useState({
    width: 600,
    height: 400,
  });
  const [isDraggingWindow, setIsDraggingWindow] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState("se-resize");
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  // Update max dimensions when window resizes
  useEffect(() => {
    const handleResize = () => {
      const newMaxWidth = window.innerWidth - 50;
      const newMaxHeight = window.innerHeight - 50;

      setFloatingSize((prev) => ({
        width: Math.min(prev.width, newMaxWidth),
        height: Math.min(prev.height, newMaxHeight),
      }));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Floating window event listeners
  useEffect(() => {
    if (isDraggingWindow) {
      const handleMove = (e) => handleWindowDragMove(e);
      const handleEnd = () => handleWindowDragEnd();

      window.addEventListener("mousemove", handleMove);
      window.addEventListener("mouseup", handleEnd);

      return () => {
        window.removeEventListener("mousemove", handleMove);
        window.removeEventListener("mouseup", handleEnd);
      };
    }
  }, [isDraggingWindow, dragStart, floatingSize]);

  useEffect(() => {
    if (isResizing) {
      const handleMove = (e) => handleResizeMove(e);
      const handleEnd = () => handleResizeEnd();

      window.addEventListener("mousemove", handleMove);
      window.addEventListener("mouseup", handleEnd);

      return () => {
        window.removeEventListener("mousemove", handleMove);
        window.removeEventListener("mouseup", handleEnd);
      };
    }
  }, [isResizing, resizeStart, resizeDirection]);

  /**
   * Toggle floating mode
   */
  const handleFloatingToggle = useCallback(() => {
    setIsOutputFloating(!isOutputFloating);
  }, [isOutputFloating]);

  /**
   * Start dragging window
   */
  const handleWindowDragStart = useCallback(
    (e) => {
      e.preventDefault();
      setIsDraggingWindow(true);
      setDragStart({
        x: e.clientX - floatingPosition.x,
        y: e.clientY - floatingPosition.y,
      });
      document.body.style.userSelect = "none";
      document.body.style.cursor = "move";
    },
    [floatingPosition]
  );

  /**
   * Handle window drag move
   */
  const handleWindowDragMove = useCallback(
    (e) => {
      if (!isDraggingWindow) return;
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      // Keep window within viewport bounds
      const maxX = window.innerWidth - floatingSize.width;
      const maxY = window.innerHeight - floatingSize.height;

      setFloatingPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    },
    [isDraggingWindow, dragStart, floatingSize]
  );

  /**
   * End window drag
   */
  const handleWindowDragEnd = useCallback(() => {
    setIsDraggingWindow(false);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  /**
   * Start resizing window
   */
  const handleResizeStart = useCallback(
    (e, direction) => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      setResizeDirection(direction);
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: floatingSize.width,
        height: floatingSize.height,
      });
      document.body.style.userSelect = "none";
      document.body.style.cursor = direction;
    },
    [floatingSize]
  );

  /**
   * Handle resize move
   */
  const handleResizeMove = useCallback(
    (e) => {
      if (!isResizing) return;

      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;

      const MAX_WIDTH = window.innerWidth - 50;
      const MAX_HEIGHT = window.innerHeight - 50;

      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;

      // Handle different resize directions
      if (resizeDirection === "se-resize") {
        // Southeast corner - resize both width and height
        newWidth = Math.max(
          MIN_WIDTH,
          Math.min(MAX_WIDTH, resizeStart.width + deltaX)
        );
        newHeight = Math.max(
          MIN_HEIGHT,
          Math.min(MAX_HEIGHT, resizeStart.height + deltaY)
        );
      } else if (resizeDirection === "ew-resize") {
        // East-West (right edge) - resize width only
        newWidth = Math.max(
          MIN_WIDTH,
          Math.min(MAX_WIDTH, resizeStart.width + deltaX)
        );
        newHeight = resizeStart.height;
      } else if (resizeDirection === "ns-resize") {
        // North-South (bottom edge) - resize height only
        newWidth = resizeStart.width;
        newHeight = Math.max(
          MIN_HEIGHT,
          Math.min(MAX_HEIGHT, resizeStart.height + deltaY)
        );
      }

      setFloatingSize({ width: newWidth, height: newHeight });
    },
    [isResizing, resizeStart, resizeDirection]
  );

  /**
   * End resize
   */
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  return {
    isOutputFloating,
    setIsOutputFloating,
    floatingPosition,
    floatingSize,
    isDraggingWindow,
    isResizing,
    handleFloatingToggle,
    handleWindowDragStart,
    handleWindowDragMove,
    handleWindowDragEnd,
    handleResizeStart,
    handleResizeMove,
    handleResizeEnd,
  };
};
